import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { garagesApi, type GarageSearchEntry } from '../../api/client';
import { useCarMakes } from '../../hooks/useCarMakes';
import styles from './GaragesSearch.module.css';

function GarageCard({ entry }: { entry: GarageSearchEntry }) {
  return (
    <Link to={`/garages/@${entry.user.username}`} className={styles.card}>
      <div className={styles.preview}>
        {entry.previewPhotoUrl ? (
          <img src={entry.previewPhotoUrl} alt="" />
        ) : (
          <span className={styles.placeholder}>🚗</span>
        )}
      </div>
      <div className={styles.info}>
        <span className={styles.displayName}>{entry.user.displayName}</span>
        <span className={styles.counts}>
          {entry.garageCarCount} car{entry.garageCarCount !== 1 ? 's' : ''}
        </span>
      </div>
    </Link>
  );
}

export function GaragesSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [displayName, setDisplayName] = useState(() => searchParams.get('displayName') ?? '');
  const [make, setMake] = useState(() => searchParams.get('make') ?? '');
  const [model, setModel] = useState(() => searchParams.get('model') ?? '');
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isNaN(p) || p < 1 ? 1 : p;
  });

  // Sync state from URL when user navigates (e.g. browser back/forward)
  useEffect(() => {
    const p = parseInt(searchParams.get('page') ?? '1', 10);
    setPage(Number.isNaN(p) || p < 1 ? 1 : p);
    setDisplayName(searchParams.get('displayName') ?? '');
    setMake(searchParams.get('make') ?? '');
    setModel(searchParams.get('model') ?? '');
  }, [searchParams]);

  const [items, setItems] = useState<GarageSearchEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { carMakes, loading: carMakesLoading, error: carMakesError } = useCarMakes();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await garagesApi.search({
        page,
        limit: 20,
        displayName: displayName.trim() || undefined,
        make: make.trim() || undefined,
        model: model.trim() || undefined,
      });
      setItems(res.items);
      setTotal(res.total);
      setTotalPages(res.totalPages);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to search garages');
    } finally {
      setLoading(false);
    }
  }, [page, displayName, make, model]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', String(page));
    if (displayName.trim()) params.set('displayName', displayName.trim());
    if (make.trim()) params.set('make', make.trim());
    if (model.trim()) params.set('model', model.trim());
    setSearchParams(params, { replace: true });
  }, [page, displayName, make, model, setSearchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  return (
    <div className={styles.page}>
      <h1>Search Garages</h1>
      <p className={styles.subtitle}>
        Browse garages by owner, car make, or model. Most recently updated garages first.
      </p>

      <form onSubmit={handleSearch} className={styles.searchForm}>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Search by display name"
          className={styles.searchInput}
        />
        <select
          value={make}
          onChange={(e) => setMake(e.target.value)}
          className={styles.searchInput}
          disabled={carMakesLoading || !!carMakesError}
        >
          <option value="">{carMakesLoading ? 'Loading makes...' : 'All makes'}</option>
          {carMakes.map((carMake) => (
            <option key={carMake.id} value={carMake.name}>{carMake.name}</option>
          ))}
        </select>
        <input
          type="text"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder="Search by model"
          className={styles.searchInput}
        />
        <button type="submit" className={styles.searchBtn}>
          Search
        </button>
      </form>
      {carMakesError && <p className={styles.error}>Could not load makes. You can still search by owner name or model.</p>}

      {loading ? (
        <p className={styles.loading}>Loading…</p>
      ) : error ? (
        <p className={styles.error}>{error}</p>
      ) : items.length === 0 ? (
        <p className={styles.empty}>No garages found.</p>
      ) : (
        <>
          <div className={styles.grid}>
            {items.map((entry) => (
              <GarageCard key={entry.user.id} entry={entry} />
            ))}
          </div>

          {totalPages > 1 && (
            <nav className={styles.pagination} aria-label="Pagination">
              <button
                type="button"
                className={styles.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className={styles.pageInfo}>
                Page {page} of {totalPages} ({total} garages)
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
